/**
 * DisclaimerModal Component
 * Shows legal disclaimer before first generation
 * Acceptance is stored locally and synced to Stripe customer.metadata when authenticated
 */

import { useState } from 'react'
import { Shield, AlertTriangle, Check, ExternalLink, FileText } from 'lucide-react'

const DISCLAIMER_VERSION = '1.0' // Bump this to force re-acceptance

export function getDisclaimerAcceptance() {
  const stored = localStorage.getItem('emd_disclaimer')
  if (!stored) return null

  try {
    const data = JSON.parse(stored)
    // Check if version matches
    if (data.version !== DISCLAIMER_VERSION) return null
    return data
  } catch {
    return null
  }
}

export function setDisclaimerAcceptance() {
  const data = {
    version: DISCLAIMER_VERSION,
    acceptedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
  }
  localStorage.setItem('emd_disclaimer', JSON.stringify(data))
  return data
}

export function hasAcceptedDisclaimer() {
  return getDisclaimerAcceptance() !== null
}

export default function DisclaimerModal({ onAccept, onDecline }) {
  const [checked, setChecked] = useState(false)
  const [isAccepting, setIsAccepting] = useState(false)

  const handleAccept = async () => {
    if (!checked) return

    setIsAccepting(true)

    // Store acceptance locally
    setDisclaimerAcceptance()

    // TODO: If user is authenticated, sync to Stripe customer.metadata
    // await syncToStripe({ disclaimer_accepted: new Date().toISOString() })

    setTimeout(() => {
      onAccept()
    }, 300)
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-2xl w-full max-w-lg border border-slate-700 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-b border-slate-700 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Conditions d'utilisation</h2>
              <p className="text-sm text-slate-400">Veuillez lire et accepter avant de continuer</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {/* Warning box */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-5">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-300 mb-1">Important</p>
                <p className="text-slate-300">
                  EMD Factory génère du contenu via intelligence artificielle.
                  Vous êtes seul responsable de l'utilisation des sites générés.
                </p>
              </div>
            </div>
          </div>

          {/* Key points */}
          <div className="space-y-4 text-sm">
            <div>
              <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary-400" />
                Propriété du contenu
              </h3>
              <p className="text-slate-400">
                Les sites générés vous appartiennent entièrement. Vous pouvez les utiliser,
                modifier et distribuer librement. KOEKI SAS ne revendique aucun droit sur vos créations.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" />
                Responsabilité
              </h3>
              <p className="text-slate-400">
                Vous vous engagez à :
              </p>
              <ul className="mt-2 space-y-1 text-slate-400">
                <li className="flex items-start gap-2">
                  <span className="text-slate-600">•</span>
                  Posséder les droits sur les noms de domaine utilisés
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-600">•</span>
                  Ne pas générer de contenu illégal, diffamatoire ou trompeur
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-600">•</span>
                  Vérifier et valider le contenu généré avant publication
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-600">•</span>
                  Respecter les lois en vigueur dans votre juridiction
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                Données personnelles
              </h3>
              <p className="text-slate-400">
                EMD Factory ne stocke aucune donnée personnelle sur ses serveurs.
                Vos clés API et préférences restent dans votre navigateur (localStorage).
                Vos propres clés API sont utilisées directement avec les fournisseurs (Anthropic, OpenAI, Google).
              </p>
            </div>
          </div>

          {/* Links to full legal docs */}
          <div className="mt-5 pt-4 border-t border-slate-800 flex flex-wrap gap-4 text-xs">
            <a href="#" className="text-primary-400 hover:text-primary-300 flex items-center gap-1">
              CGU complètes <ExternalLink className="w-3 h-3" />
            </a>
            <a href="#" className="text-primary-400 hover:text-primary-300 flex items-center gap-1">
              CGV <ExternalLink className="w-3 h-3" />
            </a>
            <a href="#" className="text-primary-400 hover:text-primary-300 flex items-center gap-1">
              Mentions légales <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-800/50 border-t border-slate-700">
          {/* Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <div className="relative mt-0.5">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                checked
                  ? 'bg-primary-500 border-primary-500'
                  : 'border-slate-600 hover:border-slate-500'
              }`}>
                {checked && <Check className="w-3 h-3 text-white" />}
              </div>
            </div>
            <span className="text-sm text-slate-300">
              J'ai lu et j'accepte les{' '}
              <span className="text-primary-400">conditions d'utilisation</span>,{' '}
              <span className="text-primary-400">CGV</span> et{' '}
              <span className="text-primary-400">politique de confidentialité</span> d'EMD Factory.
            </span>
          </label>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onDecline}
              className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium transition text-slate-300"
            >
              Refuser
            </button>
            <button
              onClick={handleAccept}
              disabled={!checked || isAccepting}
              className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                checked
                  ? 'bg-primary-500 hover:bg-primary-400 text-white'
                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
              }`}
            >
              {isAccepting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Validation...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Accepter et continuer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
